/**
 * Social-connection OAuth callback landing page — Task 112C, extended
 * Task 113E, extended Task IG-Account-Picker (multi-IG selection),
 * extended Task IG-Account-Picker v2 (publish-identity tuple).
 *
 * Mounts at `/auth/social-oauth-callback`. The provider (Meta /
 * TikTok) redirects the user here with `?state=<state>&code=<code>`
 * after they accept the consent screen.
 *
 * Picker behavior (v2 hard-cut):
 *   - Backend emits `_body: ['multiple_publish_identities_linked']`
 *     when the connecting Meta account has ≥ 2 valid publish
 *     identities (Business Manager / Page / IG tuples).
 *   - The picker UI groups candidates by Business Manager. Each row
 *     is a full tuple (Page → Instagram). Pages outside any BM are
 *     grouped under "بدون مدير تجاري" at the bottom.
 *   - Selection re-POSTs `{state, selected_publish_identity_key}` to
 *     `complete-social-connection-oauth`. The legacy
 *     `selected_instagram_business_account_id` field is NO LONGER
 *     accepted by the EF (hard-cut).
 *   - Client-relationship Business Managers display a small badge
 *     next to the BM name so operators can spot externally-revocable
 *     access.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { kmiFetch, KmiApiError } from '../../api/client';
import type { CompleteSocialConnectionOAuthData } from './SocialOAuthCallbackPage.types';

/**
 * Task 113E — sessionStorage key the publishing-page connect button
 * writes BEFORE handing off to Meta. The callback page reads it on
 * error to decide where the "إعادة المحاولة" button should land.
 */
export const KMI_OAUTH_RETRY_REDIRECT_SS_KEY = 'kmi.oauth.retryRedirectBackTo';

/**
 * Task IG-Account-Picker v2 — wire-marker the BE emits inside
 * `fields._body[0]` to signal multi-tuple branch. Hard-cut rename
 * from PR #60's `multiple_instagram_accounts_linked`.
 */
export const MULTIPLE_PUBLISH_IDENTITIES_MARKER = 'multiple_publish_identities_linked';

/**
 * Friendly Arabic copy keyed off the wire `_body` markers the
 * backend emits.
 */
const ERROR_COPY_AR: Record<string, string> = {
  no_instagram_business_account_linked:
    'لم نجد حساب Instagram Business/Creator مرتبطاً بالصفحات التي اخترتها. ' +
    'تأكد من ربط حساب Instagram بصفحة Facebook (إعدادات Instagram ← الحساب ← ' +
    'الحسابات المرتبطة ← Facebook)، ثم احذف تطبيق KMI من Facebook ← الإعدادات ← ' +
    'التطبيقات والمواقع، وأعد المحاولة لاختيار الصفحة الصحيحة.',
  pending_selection_not_found:
    'انتهت صلاحية اختيار الحساب أو لم يتم العثور عليه. الرجاء المحاولة من جديد.',
  pending_selection_already_finalized:
    'تم تأكيد اختيار الحساب مسبقاً. إذا لم يظهر الحساب في القائمة فحاول الربط مرة أخرى.',
  pending_selection_expired: 'انتهت مهلة اختيار حساب Instagram. الرجاء بدء عملية الربط من جديد.',
  selected_publish_identity_key_invalid:
    'الحساب المختار غير صالح. الرجاء اختيار حساب من القائمة المعروضة.',
  legacy_selected_field_removed:
    'هذا الإصدار من واجهة الموقع قديم. يرجى تحديث الصفحة وإعادة المحاولة.',
  // Task IG-Account-Picker v2 — Codex P1 fix (review pass 1).
  // Fires when Meta's enumeration was partial AND we'd otherwise
  // single-tuple-fast-path the connection. The user retries; the
  // next attempt typically succeeds once the Meta rate-limit clears.
  partial_enumeration_failed_with_single_candidate:
    'تعذّر الوصول إلى جميع حسابات Meta الخاصة بك أثناء عملية الربط ' +
    '(قد يكون السبب ضغط مؤقت على واجهة Meta). يُرجى المحاولة من جديد بعد قليل ' +
    'حتى نتمكن من عرض جميع خياراتك بدلاً من الربط بحساب قد لا يكون الصحيح.',
};

const GENERIC_VALIDATION_ERROR_AR =
  'تعذّر إكمال الربط بسبب بيانات غير صالحة من المنصة. حاول مرة أخرى؛ إذا استمرّت المشكلة فاتصل بفريق الدعم.';
const GENERIC_OAUTH_ERROR_AR =
  'تعذّر إكمال الربط. حاول مرة أخرى؛ إذا استمرّت المشكلة فاتصل بفريق الدعم.';

/**
 * Task IG-Account-Picker v2 — picker prompt copy. Acknowledges the
 * tuple model: BM + Page + IG, not just IG.
 */
const PICKER_PROMPT_AR =
  'تم العثور على أكثر من حساب نشر صالح في حساب Meta الخاص بك. ' +
  'اختر المدير التجاري والصفحة وحساب Instagram الذي تريد ربطه من القائمة التالية.';

const PICKER_PRIMARY_BUTTON_AR = 'تأكيد ربط الحساب';
const PICKER_GROUP_NO_BM_AR = 'بدون مدير تجاري';
const PICKER_BADGE_CLIENT_AR = 'وكالة عميلة';

/**
 * Task IG-Account-Picker v2 — full publish-identity tuple shape
 * mirrored from the BE EF's `PublishIdentityCandidate` interface.
 * Each candidate carries ONLY safe display fields. NO token / state
 * / code ever lands here.
 */
interface PublishIdentityCandidate {
  readonly key: string;
  readonly business: {
    readonly id: string;
    readonly name: string;
    readonly relationship: 'owned' | 'client';
  } | null;
  readonly page: {
    readonly id: string;
    readonly name: string;
  };
  readonly instagram: {
    readonly id: string;
    readonly username: string | null;
    readonly name: string | null;
  };
}

type CallbackState =
  | { readonly status: 'starting' }
  | { readonly status: 'success'; readonly data: CompleteSocialConnectionOAuthData }
  | {
      readonly status: 'error';
      // The friendly Arabic message rendered to the user.
      readonly messageAr: string;
      // Where the "إعادة المحاولة" button sends the user.
      readonly retryHref: string;
    }
  | {
      // Task IG-Account-Picker v2 — multi-tuple picker branch.
      readonly status: 'picker';
      readonly candidates: ReadonlyArray<PublishIdentityCandidate>;
      // The selected candidate's composite key. null = nothing
      // selected yet; the "تأكيد" button is disabled until one is
      // chosen.
      readonly selectedKey: string | null;
      // The state token from the URL — re-used as the lookup key on
      // the selection re-POST.
      readonly state: string;
      // True while the selection re-POST is in flight.
      readonly submitting: boolean;
      // Inline error displayed under the picker on a failed submit.
      readonly errorAr: string | null;
    };

function isSafeRedirectPath(value: string | null | undefined): value is string {
  if (!value) return false;
  if (!value.startsWith('/')) return false;
  if (value.startsWith('//')) return false;
  if (value.includes(':')) return false;
  return true;
}

function consumeRetryRedirect(): string {
  if (typeof window === 'undefined') return '/clients';
  let raw: string | null = null;
  try {
    raw = window.sessionStorage.getItem(KMI_OAUTH_RETRY_REDIRECT_SS_KEY);
    window.sessionStorage.removeItem(KMI_OAUTH_RETRY_REDIRECT_SS_KEY);
  } catch {
    raw = null;
  }
  if (!raw) return '/clients';
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return '/clients';
  }
  const path =
    parsed && typeof parsed === 'object' && 'path' in parsed
      ? (parsed as { path?: unknown }).path
      : null;
  return typeof path === 'string' && isSafeRedirectPath(path) ? path : '/clients';
}

/**
 * Task IG-Account-Picker v2 — parse the wire `fields.candidates[0]`
 * string into the typed candidate array.
 *
 * The BE encodes the candidates as a single JSON-stringified array.
 * Per-element validation:
 *   - `key`, `page.id`, `page.name`, `instagram.id` are required
 *   - `business` is fully required when present (id + name +
 *     relationship); a partial business object is treated as null
 *     to keep the picker safe against malformed BE responses
 *   - `relationship` must be exactly 'owned' or 'client'; anything
 *     else makes the business object null
 *   - Optional display fields (`instagram.username`, `instagram.name`)
 *     coerce to null when missing / empty
 *
 * Returns null when the entire candidate set is unparseable / empty
 * (caller falls back to generic error rendering).
 */
export function parseCandidatesFromFields(
  fields: unknown,
): ReadonlyArray<PublishIdentityCandidate> | null {
  if (!fields || typeof fields !== 'object') return null;
  const raw = (fields as Record<string, unknown>)['candidates'];
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const first = raw[0];
  if (typeof first !== 'string' || first.length === 0) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(first);
  } catch {
    return null;
  }
  if (!Array.isArray(parsed) || parsed.length === 0) return null;
  const candidates: Array<PublishIdentityCandidate> = [];
  for (const entry of parsed) {
    if (!entry || typeof entry !== 'object') continue;
    const rec = entry as Record<string, unknown>;

    const key = rec['key'];
    if (typeof key !== 'string' || key.length === 0) continue;

    const page = rec['page'];
    if (!page || typeof page !== 'object') continue;
    const pageRec = page as Record<string, unknown>;
    const pageId = pageRec['id'];
    const pageName = pageRec['name'];
    if (typeof pageId !== 'string' || pageId.length === 0) continue;
    if (typeof pageName !== 'string' || pageName.length === 0) continue;

    const instagram = rec['instagram'];
    if (!instagram || typeof instagram !== 'object') continue;
    const igRec = instagram as Record<string, unknown>;
    const igId = igRec['id'];
    if (typeof igId !== 'string' || igId.length === 0) continue;
    const igUsername = igRec['username'];
    const igName = igRec['name'];

    // Business is optional. If present, all three sub-fields must be
    // present and `relationship` must be one of the documented
    // values; otherwise treat as null (defensive against partial
    // server payloads).
    let business: PublishIdentityCandidate['business'] = null;
    const bm = rec['business'];
    if (bm && typeof bm === 'object') {
      const bmRec = bm as Record<string, unknown>;
      const bmId = bmRec['id'];
      const bmName = bmRec['name'];
      const bmRel = bmRec['relationship'];
      if (
        typeof bmId === 'string' &&
        bmId.length > 0 &&
        typeof bmName === 'string' &&
        bmName.length > 0 &&
        (bmRel === 'owned' || bmRel === 'client')
      ) {
        business = { id: bmId, name: bmName, relationship: bmRel };
      }
    }

    candidates.push({
      key,
      business,
      page: { id: pageId, name: pageName },
      instagram: {
        id: igId,
        username: typeof igUsername === 'string' && igUsername.length > 0 ? igUsername : null,
        name: typeof igName === 'string' && igName.length > 0 ? igName : null,
      },
    });
  }
  return candidates.length === 0 ? null : candidates;
}

/**
 * Task IG-Account-Picker v2 — group candidates by Business Manager
 * for the picker UI. Returns an ordered array of groups; the
 * "no BM" group (business=null) is always last.
 *
 * Within a BM group, candidates retain their server-side order
 * (which mirrors Meta's enumeration order). The first candidate
 * across all groups is NOT special — the picker requires explicit
 * user selection.
 */
function groupCandidatesByBusiness(
  candidates: ReadonlyArray<PublishIdentityCandidate>,
): ReadonlyArray<{
  readonly business: PublishIdentityCandidate['business'];
  readonly items: ReadonlyArray<PublishIdentityCandidate>;
}> {
  const bmGroups = new Map<string, Array<PublishIdentityCandidate>>();
  const noBmGroup: Array<PublishIdentityCandidate> = [];
  // Preserve insertion order (Map iteration order in JS).
  const businessByKey = new Map<string, PublishIdentityCandidate['business']>();
  for (const c of candidates) {
    if (c.business === null) {
      noBmGroup.push(c);
      continue;
    }
    const key = c.business.id;
    if (!bmGroups.has(key)) {
      bmGroups.set(key, []);
      businessByKey.set(key, c.business);
    }
    bmGroups.get(key)!.push(c);
  }
  const out: Array<{
    readonly business: PublishIdentityCandidate['business'];
    readonly items: ReadonlyArray<PublishIdentityCandidate>;
  }> = [];
  for (const [bid, items] of bmGroups) {
    out.push({ business: businessByKey.get(bid) ?? null, items });
  }
  if (noBmGroup.length > 0) {
    out.push({ business: null, items: noBmGroup });
  }
  return out;
}

function describeOAuthError(err: unknown): string {
  if (err instanceof KmiApiError) {
    const fields = err.body?.fields;
    if (fields && typeof fields === 'object') {
      const body = (fields as Record<string, ReadonlyArray<string>>)['_body'];
      const marker = Array.isArray(body) && typeof body[0] === 'string' ? body[0] : null;
      if (marker && ERROR_COPY_AR[marker]) return ERROR_COPY_AR[marker];
    }
    if (err.code === 'validation_error') return GENERIC_VALIDATION_ERROR_AR;
  }
  return GENERIC_OAUTH_ERROR_AR;
}

/**
 * Task IG-Account-Picker v2 — detect whether an error is the
 * picker-required marker. Returns the parsed candidate array on
 * match, or null otherwise.
 */
function detectMultiplePublishIdentitiesPicker(
  err: unknown,
): ReadonlyArray<PublishIdentityCandidate> | null {
  if (!(err instanceof KmiApiError)) return null;
  const fields = err.body?.fields;
  if (!fields || typeof fields !== 'object') return null;
  const body = (fields as Record<string, ReadonlyArray<string>>)['_body'];
  const marker = Array.isArray(body) && typeof body[0] === 'string' ? body[0] : null;
  if (marker !== MULTIPLE_PUBLISH_IDENTITIES_MARKER) return null;
  return parseCandidatesFromFields(fields);
}

export function SocialOAuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState<CallbackState>({ status: 'starting' });

  useEffect(() => {
    const stateParam = searchParams.get('state') ?? '';
    const codeParam = searchParams.get('code') ?? '';
    const errorParam = searchParams.get('error');
    const errorDescParam = searchParams.get('error_description');

    if (!stateParam) {
      setState({
        status: 'error',
        messageAr: 'رابط غير صالح: حقل state مفقود.',
        retryHref: consumeRetryRedirect(),
      });
      return;
    }
    if (!codeParam && !errorParam) {
      setState({
        status: 'error',
        messageAr: 'رابط غير صالح: حقل code مفقود.',
        retryHref: consumeRetryRedirect(),
      });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const data = await kmiFetch<CompleteSocialConnectionOAuthData>(
          'complete-social-connection-oauth',
          {
            method: 'POST',
            body: {
              state: stateParam,
              code: codeParam,
              error: errorParam ?? undefined,
              error_description: errorDescParam ?? undefined,
            },
          },
        );
        if (cancelled) return;
        try {
          window.sessionStorage.removeItem(KMI_OAUTH_RETRY_REDIRECT_SS_KEY);
        } catch {
          /* tolerable in privacy-mode contexts */
        }
        setState({ status: 'success', data });
        setTimeout(() => {
          if (cancelled) return;
          if (isSafeRedirectPath(data.redirect_back_to)) {
            navigate(data.redirect_back_to);
          } else {
            navigate('/clients');
          }
        }, 1200);
      } catch (err: unknown) {
        if (cancelled) return;
        // Task IG-Account-Picker v2 — detect the multi-tuple marker
        // BEFORE the generic-error fallback.
        const pickerCandidates = detectMultiplePublishIdentitiesPicker(err);
        if (pickerCandidates) {
          setState({
            status: 'picker',
            candidates: pickerCandidates,
            selectedKey: null,
            state: stateParam,
            submitting: false,
            errorAr: null,
          });
          return;
        }
        setState({
          status: 'error',
          messageAr: describeOAuthError(err),
          retryHref: consumeRetryRedirect(),
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams, navigate]);

  /**
   * Task IG-Account-Picker v2 — selection submit handler. Re-POSTs
   * to `complete-social-connection-oauth` with `{state,
   * selected_publish_identity_key}`. The legacy
   * `selected_instagram_business_account_id` field is no longer
   * accepted by the EF (hard-cut wire change).
   */
  async function handlePickerSubmit(selectedKey: string, pickerState: string): Promise<void> {
    if (state.status !== 'picker' || state.submitting) return;
    setState({ ...state, submitting: true, errorAr: null });
    try {
      const data = await kmiFetch<CompleteSocialConnectionOAuthData>(
        'complete-social-connection-oauth',
        {
          method: 'POST',
          body: {
            state: pickerState,
            selected_publish_identity_key: selectedKey,
          },
        },
      );
      try {
        window.sessionStorage.removeItem(KMI_OAUTH_RETRY_REDIRECT_SS_KEY);
      } catch {
        /* tolerable in privacy-mode contexts */
      }
      setState({ status: 'success', data });
      setTimeout(() => {
        if (isSafeRedirectPath(data.redirect_back_to)) {
          navigate(data.redirect_back_to);
        } else {
          navigate('/clients');
        }
      }, 1200);
    } catch (err: unknown) {
      const messageAr = describeOAuthError(err);
      if (state.status === 'picker') {
        setState({
          ...state,
          submitting: false,
          errorAr: messageAr,
        });
      }
    }
  }

  return (
    <div className="stack-md" style={{ maxWidth: 640, margin: '40px auto', padding: 16 }}>
      <div className="card" style={{ padding: 22 }}>
        <h2 style={{ marginTop: 0 }}>إكمال ربط الحساب</h2>
        {state.status === 'starting' ? <p>جارٍ إكمال الربط… الرجاء عدم إغلاق النافذة.</p> : null}
        {state.status === 'success' ? (
          <>
            <p>
              <span className="pill pill-active">تم الربط</span> منصة:{' '}
              <strong>{state.data.platform}</strong>
            </p>
            {state.data.capabilities.length > 0 ? (
              <p>
                الصلاحيات الممنوحة:{' '}
                {state.data.capabilities.map((c) => (
                  <span key={c} className="pill pill-info" style={{ marginInlineEnd: 6 }}>
                    {c}
                  </span>
                ))}
              </p>
            ) : null}
            <p style={{ color: 'var(--muted)' }}>سيتم إعادة توجيهك خلال لحظات…</p>
          </>
        ) : null}
        {state.status === 'picker' ? (
          <>
            <p>{PICKER_PROMPT_AR}</p>
            {/*
              Task IG-Account-Picker v2 — grouped picker UI. Candidates
              are clustered by Business Manager; each group renders a
              header (BM name + optional "client" badge) and a stack
              of radio rows (Page → Instagram). Pages outside any BM
              fall under the "بدون مدير تجاري" group at the bottom.
            */}
            <div
              role="radiogroup"
              aria-label={PICKER_PROMPT_AR}
              style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBlock: 12 }}
            >
              {groupCandidatesByBusiness(state.candidates).map((group, gIdx) => (
                <div
                  key={group.business?.id ?? `__no_bm_${gIdx}`}
                  style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                >
                  <div
                    style={{
                      fontWeight: 600,
                      color: 'var(--muted)',
                      fontSize: '0.92em',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    {group.business ? (
                      <>
                        <span>المدير التجاري: {group.business.name}</span>
                        {group.business.relationship === 'client' ? (
                          <span
                            className="pill pill-info"
                            style={{ fontSize: '0.78em' }}
                            aria-label={PICKER_BADGE_CLIENT_AR}
                          >
                            {PICKER_BADGE_CLIENT_AR}
                          </span>
                        ) : null}
                      </>
                    ) : (
                      <span>{PICKER_GROUP_NO_BM_AR}</span>
                    )}
                  </div>
                  {group.items.map((c) => {
                    const igPrimary = c.instagram.username ?? c.instagram.name ?? c.instagram.id;
                    const checked = state.selectedKey === c.key;
                    return (
                      <label
                        key={c.key}
                        style={{
                          display: 'flex',
                          gap: 10,
                          alignItems: 'flex-start',
                          padding: '10px 12px',
                          border: `1px solid ${checked ? 'var(--accent, #1f6feb)' : 'var(--border, #d0d7de)'}`,
                          borderRadius: 6,
                          cursor: state.submitting ? 'not-allowed' : 'pointer',
                          opacity: state.submitting ? 0.7 : 1,
                        }}
                      >
                        <input
                          type="radio"
                          name="publish_identity"
                          value={c.key}
                          checked={checked}
                          disabled={state.submitting}
                          onChange={() => {
                            if (state.status !== 'picker') return;
                            setState({ ...state, selectedKey: c.key });
                          }}
                          aria-label={`${c.page.name} → ${igPrimary}`}
                        />
                        <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <strong>صفحة فيسبوك: {c.page.name}</strong>
                          <span style={{ color: 'var(--muted)', fontSize: '0.9em' }}>
                            انستغرام: {igPrimary}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              ))}
            </div>
            {state.errorAr ? (
              <div className="banner banner-error" role="alert" style={{ marginBlock: 10 }}>
                {state.errorAr}
              </div>
            ) : null}
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button
                type="button"
                className="btn btn-primary"
                disabled={state.submitting || state.selectedKey === null}
                onClick={() => {
                  if (state.status !== 'picker') return;
                  if (state.selectedKey === null) return;
                  void handlePickerSubmit(state.selectedKey, state.state);
                }}
              >
                {state.submitting ? 'جارٍ الحفظ…' : PICKER_PRIMARY_BUTTON_AR}
              </button>
              <button
                type="button"
                className="btn-text"
                onClick={() => navigate(consumeRetryRedirect())}
                style={{ padding: '10px 14px' }}
                disabled={state.submitting}
              >
                إعادة المحاولة
              </button>
            </div>
          </>
        ) : null}
        {state.status === 'error' ? (
          <>
            <div className="banner banner-error" role="alert">
              {state.messageAr}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button type="button" className="btn" onClick={() => navigate(state.retryHref)}>
                إعادة المحاولة
              </button>
              <button
                type="button"
                className="btn-text"
                onClick={() => navigate('/clients')}
                style={{ padding: '10px 14px' }}
              >
                العودة للقائمة
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
